import { App, Button, Card, Form, Select, Space, Table } from 'antd';

import React, { useEffect, useState } from 'react';
import { DELETE, GET, POST, PUT } from '../../utils/apicalls';
import { API } from '../../config/api';
import LayoutHeader from '../../adminnavigation/layoutHeader';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

function BookingDetails() {
    const [isLoading, setIsLoading] = useState(false);
    const [bookingData, setBookingData] = useState<any[]>([]);
    const [courses, setCourses] = useState<any[]>([]);
    const [filteredData, setFilteredData] = useState<any[]>([]);
    const { notification } = App.useApp();
    const [form] = Form.useForm();

    const fetchAllBookingDetails = async () => {
        try {
            setIsLoading(true);
            const response: any = await GET(API.GET_ALL_BOOKING_DETAILS, {});
            setBookingData(response?.data || []);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchAllCourses = async () => {
        try {
            setIsLoading(true);
            const response: any = await GET(API.GET_ALL_COURSES, {});
            setCourses(response?.data || []);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAllBookingDetails();
        fetchAllCourses();
    }, []);

    const filterBookingData = async (values: any) => {
        try {
            setIsLoading(true);
            const response: any = await POST(API.FILTER_BOOKING_DETAILS, values);

            if (response?.status && response.data.length > 0) {
                setFilteredData(response.data);
            } else {
                setFilteredData([]);
                notification.info({ message: 'No data found for the selected course.' });
            }
        } catch (error) {
            console.error(error);
            notification.error({ message: 'An error occurred while filtering.' });
        } finally {
            setIsLoading(false);
        }
    };

    const downloadRecordAsPDF = (record: any, courses: any[]) => {
        const doc = new jsPDF();
        doc.text('Booking Details', 10, 10);

        const course = courses.find((c: any) => c.id === record.Course_id);
        const courseName = course ? course.course_title : 'Unknown';

        const rows = [
            ['User Name', record.UserName],
            ['Phone Number', record.UserPhoneNumber],
            ['Email', record.UserEmail],
            ['Address', record.Useraddress],
            ['Amount', record.amount],
            ['Payment Status', record.paymentstatus],
            ['Course', courseName],
            ['Coupon Code', record.coupenCode],
        ];

        autoTable(doc, {
            startY: 20,
            head: [['Field', 'Value']],
            body: rows,
        });

        doc.save(`Booking-${record.id}.pdf`);
    };

    const downloadFullTableAsPDF = (data: any[], courses: any[]) => {
        const doc = new jsPDF();
        doc.text('Full Booking Data', 10, 10);

        if (data.length === 0) {
            doc.text('No data available.', 10, 20);
        } else {
            const head = ['#', 'User Name', 'Phone', 'Email', 'Address', 'Amount', 'Status', 'Course', 'Coupon'];
            const body = data.map((item, idx) => {
                const course = courses.find((c: any) => c.id === item.Course_id);
                return [
                    idx + 1,
                    item.UserName,
                    item.UserPhoneNumber,
                    item.UserEmail,
                    item.Useraddress,
                    item.amount,
                    item.paymentstatus,
                    course ? course.course_title : 'Unknown',
                    item.coupenCode,
                ];
            });

            autoTable(doc, {
                startY: 20,
                head: [head],
                body: body,
            });
        }

        doc.save('Full-Booking-Data.pdf');
    };
    const submitHandler = async (values: any, record: any) => {
        try {
            setIsLoading(true);

            // Validate that paymentstatus exists and is valid
            if (!values.paymentstatus || typeof values.paymentstatus !== 'string') {
                notification.error({ message: "Please select a valid payment status." });
                setIsLoading(false);
                return;
            }

            const obj = {
                UserName: record.UserName,
                UserPhoneNumber: record.UserPhoneNumber,
                UserEmail: record.UserEmail,
                Useraddress: record.Useraddress,
                razorpayPaymentId: record.razorpayPaymentId,
                razorpaySignature: record.razorpaySignature,
                amount: record.amount,
                paymentstatus: values.paymentstatus, // Already validated
            };

            const postRequest = await PUT(API.UPDATE_BOOKING_DETAILS + record.id, obj);

            if (postRequest) {
                notification.success({ message: "Payment status updated successfully!" });
            } else {
                notification.error({ message: "Failed to update payment status" });
            }
        } catch (error) {
            console.error("Error updating payment status:", error);
            notification.error({
                message: "Something went wrong! Please try again later",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const columns = [
        {
            title: 'Index',
            dataIndex: 'index',
            render: (_: any, __: any, index: number) => index + 1,
        },
        { title: 'User Name', dataIndex: 'UserName' },
        { title: 'Phone Number', dataIndex: 'UserPhoneNumber' },
        { title: 'Email', dataIndex: 'UserEmail' },
        { title: 'Address', dataIndex: 'Useraddress' },
        { title: 'Amount', dataIndex: 'amount' },
        {
            title: 'Payment Status',
            dataIndex: 'paymentstatus',
            render: (status: string, record: any) => (
                <Select
                    defaultValue={status}
                    style={{ width: 120, fontWeight: 'bold', textTransform: 'capitalize' }}
                    onChange={(value) => submitHandler({ paymentstatus: value }, record)}
                >
                    <Select.Option value="success" style={{ color: 'green' }}>Success</Select.Option>
                    <Select.Option value="failed" style={{ color: 'red' }}>Failed</Select.Option>
                    <Select.Option value="refund" style={{ color: 'red' }}>Refund</Select.Option>
                    <Select.Option value="pending" style={{ color: 'orange' }}>Pending</Select.Option>
                </Select>
            ),
        }
        ,
        {
            title: 'Course',
            dataIndex: 'Course_id',
            render: (Course_id: number) => {
                const course = courses.find((c: any) => c.id === Course_id);
                return course ? course.course_title : 'Unknown';
            }
        },
        { title: 'Coupon Code', dataIndex: 'coupenCode' },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: any) => (
                <Space size="middle">
                    <Button type="link" onClick={() => downloadRecordAsPDF(record, courses)}>Download</Button>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <LayoutHeader />
            <h1 className="text-2xl font-bold text-center" style={{ textAlign: "center" }}>Booking Data</h1>

            <Card>
                <h2 className="text-center mt-4" >Booking Data List</h2>
                <Card className="mb-4">
                    <Form
                        form={form}
                        layout="inline"
                        onFinish={filterBookingData}
                        style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}
                    >
                        {/* Month Selector (number values) */}
                        <Form.Item label="Month" name="month">
                            <Select placeholder="Select Month" allowClear style={{ minWidth: 150 }}>
                                {[
                                    'January', 'February', 'March', 'April', 'May', 'June',
                                    'July', 'August', 'September', 'October', 'November', 'December'
                                ].map((month, index) => (
                                    <Select.Option key={index + 1} value={index + 1}>
                                        {month}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>

                        {/* Year Selector */}
                        <Form.Item label="Year" name="year">
                            <Select placeholder="Select Year" allowClear style={{ minWidth: 120 }}>
                                {[2022, 2023, 2024, 2025, 2026, 2027, 2028].map((year) => (
                                    <Select.Option key={year} value={year}>
                                        {year}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item>
                            <Button type="primary" htmlType="submit">Filter</Button>
                            <Button
                                style={{ marginLeft: 8 }}
                                onClick={() => {
                                    form.resetFields();
                                    setFilteredData([]);
                                }}
                            >
                                Reset
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>


                <Button
                    type="primary"
                    onClick={() => downloadFullTableAsPDF(filteredData.length > 0 ? filteredData : bookingData, courses)}
                    style={{ marginBottom: 16 }}
                >
                    Download Full Table as PDF
                </Button>

                <Table
                    columns={columns}
                    dataSource={filteredData.length > 0 ? filteredData : bookingData}
                    loading={isLoading}
                    pagination={{ pageSize: 10 }}
                    rowKey="id"
                    scroll={{ x: 'max-content' }}
                />
            </Card>
        </div>
    );
}

export default BookingDetails;
